import React, { useState, useEffect } from 'react';
import { user_api } from '../../services/auth';
import { fetchUserDomainsFromAPI } from '../../services/domainService';

const ManageDomainsModal = ({
  isOpen,
  user,
  allUsers = [],
  onClose,
  onSave,
}) => {
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [error, setError] = useState('');
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');

  // Sincronizar dominios cuando se abre el modal o cambia el usuario
  useEffect(() => {
    if (user && isOpen) {
      const fetchDominios = async (user) => {
        const result = await user_api.post(
          "/dominios-usuario",
          {
            id: user.id,
          }
        );
        if (result.status == 200) {
          if (result.data.success) {
            const doms = result.data.dominios.map(d => d.dominio);
            setDomains(doms);
          } else {
            setError('Error al crear el usuario: ' + result.data.msg);
          }
        } else {
          setError('Error al crear el usuario: error de conexión.');
        }
      };
      let initialDomains = [];
      fetchDominios(user);     
      setNewDomain([]);

      // Fetch latest domains from backend API POST /users/dominios-usuario
      if (user.id > 0) {
        fetchUserDomainsFromAPI(user.id).then(apiDomains => {
          if (apiDomains && apiDomains.length > 0) {
            setDomains(prev => Array.from(new Set([...prev, ...apiDomains])));
          }
        });
      }
    }
  }, [user, isOpen]);

  if (!isOpen || !user) return null;

  const validateDomain = (domain) => {
    const cleanDomain = domain.trim().toLowerCase();
    
    if (!cleanDomain) {
      setError('El dominio no puede estar vacío.');
      return false;
    }

    // Validación básica de dominio (ej. ejemplo.com, sub.ejemplo.org)
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9])+$/;
    if (!domainRegex.test(cleanDomain)) {
      setError('Por favor, ingresa un formato de dominio válido (ej: dominio.com).');
      return false;
    }

    if (domains.includes(cleanDomain)) {
      setError('Este dominio ya está asignado a este usuario.');
      return false;
    }

    setError('');
    return true;
  };

  /*
  const handleAddDomain = async (e) => {
    e.preventDefault();
    const cleanDomain = newDomain.trim().toLowerCase();

    if (validateDomain(cleanDomain)) {
      const result = await user_api.post(
        "/asignar-dominio",
        {
          id: user.id,
          domain: cleanDomain,
        }
      );
      if (result.status == 200) {
        if (result.data.success) {
          setDomains(prev => [...prev, cleanDomain]);
          setNewDomain('');
        } else {
          setError('Error al asginar dominio al usuario: ' + result.data.msg);
        }
      } else {
        setError('Error al asignar domino al usuario: error de conexión.');
      }
    }
  };
  */

  // 1. Función auxiliar para asignar un único dominio mediante la API
const assignSingleDomain = async (userId, cleanDomain) => {
  try {
    const response = await user_api.post("/asignar-dominio", {
      id: userId,
      domain: cleanDomain,
    });

    if (response.status === 200 && response.data.success) {
      return { success: true };
    } else {
      return { 
        success: false, 
        msg: response.data.msg || 'Error al asignar dominio.' 
      };
    }
  } catch (err) {
    return { success: false, msg: 'Error de conexión con el servidor.' };
  }
};

// 2. Agregar un solo dominio manualmente
const handleAddDomain = async (e) => {
  e.preventDefault();
  const cleanDomain = new String(newDomain).trim().toLowerCase();

  if (validateDomain(cleanDomain)) {
    // Si el usuario existe en base de datos, enviamos la petición POST
    if (user && user.id) {
      const result = await assignSingleDomain(user.id, cleanDomain);
      if (!result.success) {
        setError(result.msg);
        return;
      }
    }

    setDomains(prev => [...prev, cleanDomain]);
    setNewDomain('');
    setError('');
  }
};

// 3. Copiar todos los dominios de un usuario de plantilla (Emite 1 POST por cada dominio)
const handleImportFromUser = async (templateUser) => {
  if (!templateUser || !user) return;

  // Obtener los dominios del usuario plantilla
  let templateDomains = templateUser.dominios || [];

  // Si también consultas la API para los dominios del usuario plantilla:
  if (templateUser.id) {
    const apiDomains = await fetchUserDomainsFromAPI(templateUser.id);
    if (apiDomains && apiDomains.length > 0) {
      templateDomains = Array.from(new Set([...templateDomains, ...apiDomains]));
    }
  }

  // Filtrar solo los dominios que el usuario destino aún NO tiene
  const newDomainsToAssign = templateDomains
    .map(d => String(d).trim().toLowerCase())
    .filter(d => d && !domains.includes(d));

  if (newDomainsToAssign.length === 0) {
    setError('Este cliente no tiene nuevos dominios para copiar.');
    return;
  }

  // Iterar y emitir un POST por cada dominio nuevo
  const successfullyAdded = [];
  for (const domain of newDomainsToAssign) {
    if (user.id) {
      const result = await assignSingleDomain(user.id, domain);
      if (result.success) {
        successfullyAdded.push(domain);
      } else {
        console.warn(`No se pudo asignar el dominio ${domain}: ${result.msg}`);
      }
    } else {
      // Si se está editando en estado local antes de guardar
      successfullyAdded.push(domain);
    }
  }

  // Actualizar el estado con los dominios asignados con éxito
  if (successfullyAdded.length > 0) {
    setDomains(prev => Array.from(new Set([...prev, ...successfullyAdded])));
    setTemplateSearchQuery('');
    setError('');
  } else {
    setError('No se pudo asignar ninguno de los dominios.');
  }
};

  const handleRemoveDomain = async (domainToRemove) => {
    const result = await user_api.post(
      "/borrar-dominio",
      {
        id: user.id,
        domain: domainToRemove,
      }
    );
    if (result.status == 200) {
      if (result.data.success) {
        setDomains(prev => prev.filter(d => d !== domainToRemove));
      } else {
        setError('Error desvincular el dominio: ' + result.data.msg);
      }
    } else {
      setError('Error al desvincular el domino del usuario: error de conexión.');
    }
  };

  // Filter client users with domains for copy feature
  const filteredImportUsers = allUsers.filter(u => {
    if (!user || u.id === user.id || u.rol !== 'cliente') return false;
    if (!templateSearchQuery.trim()) return true;
    const q = templateSearchQuery.toLowerCase();
    const nameMatch = u.nombre.toLowerCase().includes(q);
    const numMatch = (u.numcliente || '').toLowerCase().includes(q);
    const emailMatch = u.correo.toLowerCase().includes(q);
    const domainMatch = (u.dominios || []).some(d => String(d).toLowerCase().includes(q));
    return nameMatch || numMatch || emailMatch || domainMatch;
  });

  const subtractSets = (setA, setB) => {
    if (!(setA instanceof Set) || !(setB instanceof Set)) {
        throw new TypeError("Both arguments must be instances of Set");
    }

    const result = new Set();
    for (const value of setA) {
        if (!setB.has(value)) {
            result.add(value);
        }
    }
    return result;
}
  const handleSave = () => {
    onSave(user.id, domains);
    setTemplateSearchQuery('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-gray-100 dark:border-slate-800 w-full max-w-lg overflow-hidden transition-all duration-300 transform scale-100 flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between bg-cyan-900 text-white">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-950/40">
              <i className="fas fa-globe text-white text-lg"></i>
            </div>
            <div>
              <h3 className="text-lg font-black tracking-tight">Asignar Dominios Web</h3>
              <p className="text-xs text-cyan-200">Gestiona los dominios autorizados de la cuenta</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-cyan-850 hover:bg-cyan-800 flex items-center justify-center transition text-cyan-200 hover:text-white"
            title="Cerrar"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* User context card */}
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-850 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between">
          <div>
            <span className="text-[10px] font-bold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider block">Usuario Seleccionado</span>
            <span className="text-sm font-bold text-gray-800 dark:text-slate-100">{user.nombre}</span>
          </div>
          <div className="text-right">
            <span className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider block">Rol</span>
            <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wide inline-block ${
              user.rol === 'especialista' 
                ? 'bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-300' 
                : 'bg-cyan-100 dark:bg-cyan-500/20 text-cyan-600 dark:text-cyan-300'
            }`}>
              {user.rol}
            </span>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {/* Form to add domain */}
          <form onSubmit={handleAddDomain} className="space-y-2">
            <label className="block text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
              Añadir Nuevo Dominio
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400 dark:text-slate-500 text-sm">
                  <i className="fas fa-link"></i>
                </div>
                <input
                  type="text"
                  placeholder="ejemplo.com"
                  value={newDomain}
                  onChange={(e) => {
                    if (e.target.value !== '') setNewDomain(e.target.value);
                    setError('');
                  }}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100 text-sm placeholder-gray-400 dark:placeholder-slate-500 font-medium"
                />
              </div>
              <button
                type="submit"
                className="bg-cyan-600 hover:bg-cyan-700 text-white px-5 rounded-xl font-bold text-sm transition shadow-lg shadow-cyan-100 dark:shadow-none hover:scale-102 active:scale-98 flex items-center justify-center space-x-1.5 flex-shrink-0"
              >
                <i className="fas fa-plus"></i>
                <span>Agregar</span>
              </button>
            </div>
            {error && (
              <p className="text-xs font-bold text-red-500 dark:text-red-400 flex items-center gap-1.5 animate-pulse">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </p>
            )}
          </form>

          {/* Import from template option with search bar */}
          {allUsers.filter(u => u.id !== user.id && u.rol === 'cliente').length > 0 && (
            <div className="p-3.5 bg-slate-50 dark:bg-slate-850 rounded-2xl border border-gray-100 dark:border-slate-800 space-y-2">
              <label className="block text-[11px] font-bold text-gray-600 dark:text-slate-300 uppercase tracking-wider flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <i className="fas fa-copy text-cyan-600 dark:text-cyan-400"></i>
                  <span>Copiar dominios desde otro cliente</span>
                </span>
              </label>

              <div className="relative">
                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                <input
                  type="text"
                  placeholder="Buscar cliente origen..."
                  value={templateSearchQuery}
                  onChange={(e) => setTemplateSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-7 py-2 border border-gray-200 dark:border-slate-700 rounded-xl outline-none bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100 text-xs font-medium"
                />
                {templateSearchQuery && (
                  <button
                    type="button"
                    onClick={() => setTemplateSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>

              {/* Search results list */}
              {templateSearchQuery.trim().length > 0 && (
                <div className="max-h-40 overflow-y-auto bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-lg divide-y divide-gray-100 dark:divide-slate-700/50 text-xs">
                  {filteredImportUsers.length === 0 ? (
                    <div className="p-2.5 text-center text-gray-400 italic">
                      No hay clientes con ese criterio
                    </div>
                  ) : (
                    filteredImportUsers.map(templateUser => (
                      <div
                        key={templateUser.id}
                        className="p-2.5 hover:bg-cyan-50/60 dark:hover:bg-slate-700/50 transition flex items-center justify-between gap-2"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-gray-800 dark:text-slate-100 truncate">{templateUser.nombre}</p>
                          <p className="text-[10px] text-gray-400 truncate">{templateUser.numcliente ? `#${templateUser.numcliente} • ` : ''}{templateUser.correo}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleImportFromUser(templateUser)}
                          className="bg-cyan-600 hover:bg-cyan-700 text-white px-2.5 py-1 rounded-lg font-bold text-[10px] shrink-0 transition"
                        >
                          Copiar {templateUser.dominios?.length} dominios
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* List of assigned domains */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider flex items-center justify-between">
              <span>Dominios Asignados ({domains.length})</span>
              {domains.length > 0 && (
                <span className="text-[10px] text-gray-400 normal-case font-medium">Haz clic en la papelera para desvincular</span>
              )}
            </h4>

            {domains.length === 0 ? (
              <div className="text-center py-8 px-4 border-2 border-dashed border-gray-100 dark:border-slate-800 rounded-2xl">
                <div className="w-12 h-12 bg-slate-50 dark:bg-slate-850 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-400 dark:text-slate-600">
                  <i className="fas fa-globe text-xl"></i>
                </div>
                <p className="text-xs font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">Sin Dominios</p>
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">Este usuario no tiene ningún dominio asignado todavía.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50 dark:divide-slate-800 border border-gray-100 dark:border-slate-800 rounded-2xl overflow-hidden bg-gray-50/50 dark:bg-slate-900/50 max-h-56 overflow-y-auto">
                {domains.map((domain) => (
                  <div 
                    key={domain} 
                    className="flex items-center justify-between py-2.5 px-4 hover:bg-cyan-50/20 dark:hover:bg-cyan-950/20 transition group"
                  >
                    <div className="flex items-center space-x-2.5 min-w-0">
                      <div className="w-6 h-6 rounded-lg bg-cyan-100/55 dark:bg-cyan-950/55 text-cyan-600 dark:text-cyan-400 flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-globe text-xs"></i>
                      </div>
                      <span className="text-sm font-semibold text-gray-700 dark:text-slate-200 truncate">{domain}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveDomain(domain)}
                      className="p-1.5 text-gray-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                      title={`Eliminar dominio ${domain}`}
                    >
                      <i className="fas fa-trash-alt text-sm"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 dark:border-slate-800 flex justify-end gap-3 bg-slate-50 dark:bg-slate-850">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition font-bold text-xs uppercase tracking-wider"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-5 py-2.5 rounded-xl bg-cyan-600 text-white hover:bg-cyan-700 transition shadow-lg shadow-cyan-100 dark:shadow-none font-bold text-xs uppercase tracking-wider flex items-center space-x-2"
          >
            <i className="fas fa-save"></i>
            <span>Guardar Cambios</span>
          </button>
        </div>

      </div>
    </div>
  );
};

export default ManageDomainsModal;
